<template>
  <Sheet :open="true" @update:open="handleCancel">
    <SheetContent>
      <SheetHeader padded>
        <SheetTitle size="lg">Move to Rejected</SheetTitle>
      </SheetHeader>

      <Separator />

      <SheetBody padded>
        <p class="text-sm font-medium text-gray-700 mb-4">Send rejection via:</p>

        <div class="space-y-3 mb-6">
          <RejectionMethodOption
            title="Email"
            :description="
              postmarkVerified ? 'Send rejection email' : 'Verify your personal email to send emails to candidates.'
            "
            :selected="sendVia === 'email'"
            :disabled="!postmarkVerified"
            @select="sendVia = 'email'"
          >
            <template v-if="!postmarkVerified" #badge>
              <PersonalEmailUnverified />
            </template>
          </RejectionMethodOption>
          <RejectionMethodOption
            title="SMS"
            :description="canReceiveSms ? 'Send rejection text' : 'Candidate did not opt-in to SMS'"
            :selected="sendVia === 'sms'"
            :disabled="!canReceiveSms"
            @select="sendVia = 'sms'"
          />
          <RejectionMethodOption
            title="None"
            description="Don't send a rejection message"
            :selected="sendVia === 'none'"
            @select="sendVia = 'none'"
          />
        </div>

        <div v-if="sendVia === 'email'">
          <Separator />
          <h2 class="text-sm font-medium mb-2">Email Message</h2>
          <EmailInput
            v-model:subject="rejectionEmail.subject"
            v-model:body="rejectionEmail.body"
            :delivery-mode="'user'"
          />
        </div>

        <div v-if="sendVia === 'sms'">
          <Separator />
          <h2 class="text-sm font-medium mb-2">SMS Message</h2>
          <ChatInput v-model="rejectionSms" />
        </div>

        <Separator class="my-6" />

        <div class="flex items-start justify-between gap-4">
          <div>
            <p class="text-sm font-medium text-gray-700">Allow reapplication</p>
            <p class="text-sm text-gray-500">Let this candidate reapply to other roles.</p>
          </div>
          <Switch v-model="allowReapply" />
        </div>
      </SheetBody>

      <SheetFooter bordered class="flex-row justify-end gap-3">
        <Button variant="outline" @click="handleCancel">Cancel</Button>
        <Button variant="destructive" :disabled="isSubmitting" @click="handleDisqualify"> Move to Rejected </Button>
      </SheetFooter>
    </SheetContent>
  </Sheet>
</template>

<script setup lang="ts">
import { ref } from 'vue';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetBody, SheetFooter } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { Switch } from '@/components/ui/switch';
import { toast } from 'vue-sonner';
import { Api } from '@/api';
import RejectionMethodOption from './RejectionMethodOption.vue';
import ChatInput from '@/components/shared/ChatInput.vue';
import EmailInput from '@/components/shared/EmailInput.vue';
import PersonalEmailUnverified from '@/components/shared/EmailBadge/PersonalEmailUnverified.vue';
import { defaultContent } from './defaultContent';
import { useCurrentUserStore } from '@/stores/currentUser';

type SendVia = 'email' | 'sms' | 'none';

type Props = {
  contact: { id: number; canReceiveSms?: boolean };
};

const props = defineProps<Props>();

const userStore = useCurrentUserStore();
const postmarkVerified = userStore.currentUser?.postmarkEmailVerified ?? false;
const canReceiveSms = props.contact.canReceiveSms ?? false;

const emit = defineEmits<{
  handleContactDisqualify: [];
  handleContactDisqualifyCancel: [];
}>();

const sendVia = ref<SendVia>('none');
const allowReapply = ref(true);
const isSubmitting = ref(false);
const rejectionSms = ref(defaultContent.smsMessage);
const rejectionEmail = ref({ ...defaultContent.email });

const handleDisqualify = async (): Promise<void> => {
  const errors = validateFields();
  if (errors.length > 0) {
    toast.error('Candidate disqualification error', {
      description: errors.join(', '),
    });
    return;
  }

  isSubmitting.value = true;

  const response = await Api.Contacts.updateContact({
    contactId: props.contact.id,
    payload: {
      stage: 'Disqualified',
      reapply_allowed: allowReapply.value,
      send_rejection_email: sendVia.value === 'email' ? true : undefined,
      rejection: {
        send_email: sendVia.value === 'email',
        send_sms: sendVia.value === 'sms',
        sms_message: rejectionSms.value,
        email_body: rejectionEmail.value.body,
        email_subject: rejectionEmail.value.subject,
      },
    },
  });

  isSubmitting.value = false;

  if (response.status !== 'error') {
    emit('handleContactDisqualify');
    toast.success('Candidate disqualified', {
      description: 'Your candidate has been successfully disqualified.',
    });
  } else {
    toast.error('Candidate disqualification error', {
      description: response.message,
    });
  }
};

const validateFields = () => {
  const errors = [];
  if (sendVia.value === 'email') {
    const blankBody = '<p><br></p>';
    if (!rejectionEmail.value.subject) errors.push("Email subject can't be blank");
    if (!rejectionEmail.value.body || rejectionEmail.value.body === blankBody) errors.push("Email body can't be blank");
  }

  if (sendVia.value === 'sms') {
    if (!rejectionSms.value) errors.push("SMS message can't be blank");
  }

  return errors;
};

const handleCancel = (): void => {
  emit('handleContactDisqualifyCancel');
};
</script>
